import React, { useState } from "react";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
} from "@/v2-components/ui/sheet";
import { Tooltip } from "@/v2-components/ui/tooltip";
import CrossIcon from "@/v2-components/core/icon/cross-icon";
import InfoIcon from "@/v2-components/core/icon/info-icon";
import { useRouter } from "next/router";
import { SUPPORTED_GAMES_HREF } from "@/v2-components/pages/games/GamesSubNav";
import { useTrans } from "@/utils/useTrans";
import { Button } from "@/v2-components/ui/button";
import { useIsMobileView } from "@/contexts/ResponsiveProvider";
import { cn } from "@/libs/utils";
import { DeviceType } from "@/services/ContentsService";
import DeviceUtil from "@/utils/DeviceUtil";
import Image from "next/image";

type DeviceTypeExcludeHand = Exclude<DeviceType, DeviceType.Hand>;

const IMG_BASE = "/images/compatibility/meta-quest";

type Headset = {
  /** 고유명사라 번역하지 않음 */
  label: string;
  img: string;
  /** 있으면 카드 우상단에 ⓘ 툴팁 노출 (번역 대상) */
  tooltip?: string;
};

type BannerVariant = "plain" | "info";

type ProductTab = {
  /** 탭 라벨은 DeviceUtil.getFriendlyName(device), "Check compatible games" 필터도 이 값 사용 */
  device: DeviceTypeExcludeHand;
  banner?: { text: string; variant: BannerVariant };
  brands: { brand: string; items: Headset[] }[];
};

/* 헤드셋 단건 정의 — 탭 간 재사용 */
const HEADSETS = {
  quest2: { label: "Meta Quest 2", img: `${IMG_BASE}/quest-2.png` },
  questPro: { label: "Meta Quest Pro", img: `${IMG_BASE}/quest-pro.png` },
  quest3: { label: "Meta Quest 3", img: `${IMG_BASE}/quest-3.png` },
  quest3s: { label: "Meta Quest 3S", img: `${IMG_BASE}/quest-3s.png` },
  pico4: { label: "PICO 4", img: `${IMG_BASE}/pico-4.png` },
  pico4Pro: { label: "PICO 4 Pro", img: `${IMG_BASE}/pico-4-pro.png` },
  pico4Ultra: { label: "PICO 4 Ultra", img: `${IMG_BASE}/pico-4-ultra.png` },
  picoNeo3: {
    label: "PICO Neo 3",
    img: `${IMG_BASE}/pico-neo-3.png`,
    tooltip:
      "PICO Neo 3 is supported only in developer & business environments",
  },
  viveFocus3: {
    label: "VIVE Focus 3",
    img: `${IMG_BASE}/htc-vive-focus-3.png`,
  },
  viveFocusVision: {
    label: "VIVE Focus Vision",
    img: `${IMG_BASE}/htc-vive-focus-vision.png`,
  },
  viveXrElite: {
    label: "VIVE XR Elite",
    img: `${IMG_BASE}/htc-vive-xr-elite.png`,
  },
  psvr2: { label: "PSVR2", img: `${IMG_BASE}/psvr2.png` },
  valveIndex: { label: "Valve Index", img: `${IMG_BASE}/valve-index.png` },
  varjoXr3: { label: "Varjo XR-3", img: `${IMG_BASE}/varjo-xr-3.png` },
  varjoXr4: { label: "Varjo XR-4", img: `${IMG_BASE}/varjo-xr-4.png` },
};

/* 브랜드 그룹 — 탭 간 재사용 */
const META = {
  brand: "Meta",
  items: [
    HEADSETS.quest2,
    HEADSETS.questPro,
    HEADSETS.quest3,
    HEADSETS.quest3s,
  ],
};
const PICO_FULL = {
  brand: "PICO",
  items: [
    HEADSETS.pico4,
    HEADSETS.pico4Pro,
    HEADSETS.pico4Ultra,
    HEADSETS.picoNeo3,
  ],
};
const PICO_NO_NEO = {
  brand: "PICO",
  items: [HEADSETS.pico4, HEADSETS.pico4Pro, HEADSETS.pico4Ultra],
};
const HTC_VIVE = {
  brand: "HTC VIVE",
  items: [HEADSETS.viveFocus3, HEADSETS.viveFocusVision, HEADSETS.viveXrElite],
};
const PSVR = { brand: "PSVR", items: [HEADSETS.psvr2] };
const VALVE = { brand: "Valve", items: [HEADSETS.valveIndex] };
const VARJO = {
  brand: "Varjo",
  items: [HEADSETS.varjoXr3, HEADSETS.varjoXr4],
};

const STEAM_VR_BANNER = {
  text: "All Steam VR compatible headsets (PC VR Headset)",
  variant: "plain" as const,
};

/* 탭 순서/구성 = Figma 시안 그대로 (TactSuit → TactSleeve → TactVisor → TactGlove → Tactosy) */
const PRODUCT_TABS: ProductTab[] = [
  {
    device: DeviceType.Vest, // TactSuit Pro & Air
    banner: STEAM_VR_BANNER,
    brands: [META, PICO_FULL, HTC_VIVE, PSVR, VARJO],
  },
  {
    device: DeviceType.Arm, // TactSleeve
    banner: STEAM_VR_BANNER,
    brands: [META, PICO_FULL, HTC_VIVE, PSVR, VARJO],
  },
  {
    device: DeviceType.Head, // TactVisor
    banner: {
      text: "A third-party facial interface with Velcro attachment may need to be purchased separately.",
      variant: "info",
    },
    brands: [META, PICO_NO_NEO, HTC_VIVE, PSVR, VALVE, VARJO],
  },
  {
    device: DeviceType.Glove, // TactGlove
    banner: {
      text: "Content that supports TactGlove is still limited.",
      variant: "info",
    },
    brands: [META],
  },
  {
    device: DeviceType.Foot, // Tactosy for Feet
    banner: STEAM_VR_BANNER,
    brands: [META, PICO_FULL, HTC_VIVE, PSVR, VARJO],
  },
];

/**
 * 브랜드 그룹을 플레이어별로 좁히는 필터 (#5475).
 *
 * Software 페이지는 플레이어 셋이 각자 이 시트를 여는데, 그대로 열면 **그 플레이어와
 * 무관한 헤드셋이 대부분**이다 — 예를 들어 Mobile Player(PS VR2)에서 Quest·PICO·VIVE·
 * Varjo 가 다 보였다.
 *
 * ⚠️ **기본값은 `undefined` = 전체**다. 도입 당시 기존 소비처(`GameMetaQuest`·
 * `GameSteamVR`)가 프롭을 안 줘서 동작이 그대로였다.
 *
 * ★그 둘은 #5689 가 지웠다(2026-09-16). 남은 소비처는 `sw-player-section.tsx` ·
 * `sw-config.ts` 둘이고 **둘 다 프롭을 준다** — 즉 지금 `undefined` 폴백은 **호출처가
 * 0곳인 기본값**이다. 걷지 않는 이유: 이 타입은 공개 prop 면이고 프롭을 안 주는 호출이
 * 언제든 정당하게 생긴다(「전체 헤드셋」이 유효한 요청이다).
 */
export type HeadsetAudience = "pcvr" | "standalone" | "psvr";

/** 어떤 브랜드가 어느 플레이어에 해당하나. 브랜드 이름은 위 상수의 `brand` 와 같다. */
const AUDIENCE_BRANDS: Record<HeadsetAudience, string[]> = {
  // PC Player — Steam VR 로 구동. PSVR2 는 PC 로 안 붙는다.
  pcvr: ["Meta", "PICO", "HTC VIVE", "Valve", "Varjo"],
  // VR Player — 헤드셋 단독 구동. PC 전용(Valve·Varjo)과 PSVR 제외.
  standalone: ["Meta", "PICO", "HTC VIVE"],
  // Mobile Player — PS VR2 + 휴대폰.
  psvr: ["PSVR"],
};

/*
 sheet 진입점
 */
export const CompatibleVrHeadsetsSheets = ({
  open,
  setOpen,
  audience,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** 안 주면 전체(기존 동작). */
  audience?: HeadsetAudience;
}) => {
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <CompatibleVrHeadsetsSheetBody audience={audience} setOpen={setOpen} />
    </Sheet>
  );
};

/*
 sheet body — 데스크탑(우측 drawer 634px) / 모바일(하단 bottom-sheet) 반응형
 */
const CompatibleVrHeadsetsSheetBody = ({
  audience,
  setOpen,
}: {
  audience?: HeadsetAudience;
  /** CTA 가 이동하면서 시트를 닫는다 — 안 닫으면 이동한 화면이 시트에 가린다(#5566). */
  setOpen: (open: boolean) => void;
}) => {
  const router = useRouter();
  const { t } = useTrans(["games"]);
  const isMobile = useIsMobileView();

  const [activeDevice, setActiveDevice] = useState<DeviceTypeExcludeHand>(
    PRODUCT_TABS[0].device,
  );
  // ⚠️ 초기 탭이 필터로 사라질 수 있다 — 아래 `activeTab` 이 `tabs[0]` 으로 폴백한다.
  /**
   * `audience` 가 오면 **탭과 브랜드를 함께** 거른다.
   *
   * ★초판은 브랜드만 걸렀고, 빈 탭이 되면 「빈 화면보다 전체가 낫다」로 원본을 되돌렸다.
   * 그 폴백이 정확히 **Mobile Player(psvr)에서 TactGlove 를 누르면 Meta 가 뜨는** 결함을
   * 만들었다 — Glove 탭의 브랜드가 `[META]` 뿐이라 psvr 로 거르면 항상 비기 때문이다.
   * 해당 브랜드가 없는 제품은 **탭 자체를 감춘다** — 없는 조합을 있는 것처럼 보이면 안 된다.
   */
  const tabs = audience
    ? PRODUCT_TABS.map((tab) => ({
        ...tab,
        brands: tab.brands.filter((b) =>
          AUDIENCE_BRANDS[audience].includes(b.brand),
        ),
      })).filter((tab) => tab.brands.length > 0)
    : PRODUCT_TABS;

  const activeTab = tabs.find((tab) => tab.device === activeDevice) ?? tabs[0];

  return (
    <SheetContent
      // side 만 JS 분기(Radix slide 애니메이션이 prop 값 필요). 너비/모서리는 CSS 로:
      // laptop:min-w-[634px] = 다른 시트(cart-sheet 등) 공통 idiom, bottom 의 rounded-t-2xl 은 sheetVariants 가 제공.
      side={isMobile ? "bottom" : "right"}
      aria-describedby={undefined}
      className="flex flex-col gap-0 overflow-hidden p-0 laptop:min-w-[634px] laptop:p-0"
    >
      {/* Header */}
      <header className="flex h-16 shrink-0 items-center justify-between px-4 laptop:h-auto laptop:px-8 laptop:py-7">
        <div className="flex flex-col gap-2">
          <SheetTitle className="text-base font-500 text-coolgray-90 laptop:text-[22px] laptop:font-400 laptop:leading-[28px]">
            {t("Compatible VR Headsets")}
          </SheetTitle>
          <p className="hidden text-sm leading-[18px] tracking-[0.25px] text-coolgray-50 laptop:block">
            {t("Check whether your VR headset is compatible")}
          </p>
        </div>
        <SheetClose className="shrink-0 focus:outline-none">
          <CrossIcon className="h-6 w-6" />
        </SheetClose>
      </header>

      {/* Body (scrollable). scrollbar-gutter:stable = 스크롤바 자리 항상 예약 →
          탭 전환으로 콘텐츠 높이가 바뀌어 스크롤바가 생겼다 사라져도 안쪽 폭이 불변 →
          탭+배너+이미지가 좌우로 밀리지 않음. Windows 클래식 스크롤바(~15px)에서만 보이던
          이동 현상 해결(맥 오버레이는 gutter 0이라 no-op). (pitfalls #17) */}
      <section className="flex flex-1 flex-col gap-5 overflow-y-auto px-4 pb-5 [scrollbar-gutter:stable] laptop:px-8">
        <ProductTabMenu
          activeDevice={activeTab.device}
          setActiveDevice={setActiveDevice}
          tabs={tabs}
        />

        {activeTab.banner && <Banner {...activeTab.banner} />}

        <div className="flex flex-col gap-3 laptop:gap-5">
          {activeTab.brands.map((group) => (
            <BrandGroupSection key={group.brand} {...group} />
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="shrink-0 border-t border-coolgray-20 p-4 laptop:p-8">
        <Button
          variant="blue-fill"
          shape="pill"
          size="none"
          className="h-10 w-full text-xs laptop:h-[54px] laptop:text-base"
          onClick={() => {
            /**
             * ★**VR 게임 목록으로 이동하며 시트를 닫는다** (#5566).
             *
             * 종전엔 `setFilters([activeDevice])` 하나뿐이었다. 그 훅의 `handleQueries`
             * 는 `pathname: router.pathname` 으로 **현재 페이지에 머물면서** 쿼리만 바꾼다:
             *
             * ```
             *   전  /games/vr/software/
             *   후  /games/vr/software/?filter=Vest&sort=monthlyPlayCount&only-sellable=false
             *       시트는 그대로 열려 있다  ← 실측 2026-09-08 dev
             * ```
             *
             * 그래서 사용자에겐 **아무 반응 없는 버튼**으로 보였다. 이 시트를 여는 화면
             * (`/games/vr/software`)에는 게임 목록이 아예 없어 더 그렇다.
             *
             * ⚠️ 옛 쿼리(`filter`·`sort`·`only-sellable`)는 **레거시 목록 규약**이다.
             *    목적지 `/games/vr/games` 는 `use-vr-games-filters.ts` 의 `QUERY_KEYS` 를
             *    읽으므로 기기 축은 **`device`** 다. 값 공간은 둘 다 `DeviceType` 이라
             *    그대로 넘긴다(`DEVICE_FACET_ORDER` 가 같은 enum 이다).
             *
             * ⚠️ **닫기를 먼저** 한다 — 이동 후에 닫으면 목적지에서 한 프레임 시트가 보인다.
             */
            setOpen(false);
            router.push({
              pathname: SUPPORTED_GAMES_HREF.vr,
              query: { device: activeDevice },
            });
          }}
        >
          {t("Check compatible games")}
        </Button>
      </footer>
    </SheetContent>
  );
};

/*
 제품 탭 (밑줄형 단일 단) — 모바일 가로 스크롤 / 데스크탑 균등 분배
 */
const ProductTabMenu = ({
  activeDevice,
  setActiveDevice,
  tabs,
}: {
  activeDevice: DeviceTypeExcludeHand;
  setActiveDevice: (device: DeviceTypeExcludeHand) => void;
  /** 이미 `audience` 로 걸러진 목록. 원본을 그리면 감춘 탭이 그대로 보인다. */
  tabs: ProductTab[];
}) => {
  return (
    <div
      role="tablist"
      aria-label="Compatible VR headsets tabs"
      // shrink-0 필수: overflow-x-auto(스크롤 컨테이너)는 flex-col 부모 안에서 min-h-0 → 미설정 시 높이 0으로 collapse.
      // 데스크탑은 Figma 원안대로 justify-between(hug-width 탭 + 균등 space-between) — flex-1 균등폭이면
      // 짧은 탭 주변 여백이 커져 간격이 불균일해 보임.
      className="scrollbar-none flex w-full shrink-0 gap-6 overflow-x-auto laptop:justify-between laptop:gap-0 laptop:overflow-visible"
    >
      {/* ★필터된 `tabs` 를 그린다 — 원본을 그리면 감춘 탭이 그대로 보인다. */}
      {tabs.map((tab) => {
        const isActive = tab.device === activeDevice;
        const label = DeviceUtil.getFriendlyName(tab.device);
        return (
          <button
            key={tab.device}
            role="tab"
            aria-selected={isActive}
            onClick={() => setActiveDevice(tab.device)}
            className={cn(
              "grid h-10 shrink-0 place-items-center whitespace-nowrap border-b-2 px-1 transition-colors",
              isActive
                ? "border-blue-60 text-coolgray-90"
                : "border-transparent text-coolgray-50 hover:text-coolgray-90",
            )}
          >
            {/* active 전환 시 font-600 으로 굵어지며 폭이 바뀌어 탭이 "움찔"하는 reflow 방지:
                항상 bold 너비를 차지하는 invisible ghost 를 같은 그리드 셀에 겹쳐 폭 고정 */}
            <span
              aria-hidden
              className="invisible col-start-1 row-start-1 text-sm font-600 tracking-[0.25px]"
            >
              {label}
            </span>
            <span
              className={cn(
                "col-start-1 row-start-1 text-sm tracking-[0.25px]",
                isActive ? "font-600" : "font-400",
              )}
            >
              {label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

/*
 안내 배너 — plain(회색 가운데 텍스트) / info(주황 ⓘ + 좌측 텍스트)
 */
const Banner = ({
  text,
  variant,
}: {
  text: string;
  variant: BannerVariant;
}) => {
  const { t } = useTrans(["games"]);

  return (
    <div
      className={cn(
        "flex items-center rounded-lg bg-coolgray-10 px-4 py-2.5 laptop:py-4",
        variant === "info" ? "gap-3" : "justify-center",
      )}
    >
      {variant === "info" && (
        <InfoIcon className="size-6 shrink-0 text-orange-50" />
      )}
      <p
        className={cn(
          "text-xs leading-4 tracking-[0.4px] text-coolgray-90",
          variant === "info" ? "flex-1" : "text-center",
        )}
      >
        {t(text)}
      </p>
    </div>
  );
};

/*
 브랜드 그룹 (라벨 + 헤드셋 그리드)
 */
const BrandGroupSection = ({
  brand,
  items,
}: {
  brand: string;
  items: Headset[];
}) => {
  return (
    <div className="flex flex-col">
      <p className="text-xs leading-4 tracking-[0.4px] text-coolgray-50 laptop:text-sm laptop:leading-[18px] laptop:tracking-[0.25px]">
        {brand}
      </p>
      {/* grid 분수 컬럼(1fr): 카드가 셀을 채우며 폭 따라 커짐(우측 여백 없음). 컬럼수만
          breakpoint 로 2→3→4 (xsm 500 / sm 640). 데스크탑(고정 634px 시트)도 sm:4열 그대로
          → 4열. grid 라서 스크롤바 폭(클래식 ~15px)에도 항상 지정 열수 유지(맥 4/윈도우 3
          깨짐 방지). 카드 높이는 aspect-ratio 로 비율 고정. (pitfalls #16) */}
      <div className="grid grid-cols-2 gap-2 xsm:grid-cols-3 sm:grid-cols-4 laptop:gap-0.5">
        {items.map((item) => (
          <HeadsetCard key={item.label} item={item} />
        ))}
      </div>
    </div>
  );
};

/*
 헤드셋 카드 (이미지 + 라벨 + 선택적 ⓘ 툴팁) — 순수 표시 컴포넌트
 */
const HeadsetCard = ({ item }: { item: Headset }) => {
  return (
    <div className="relative flex flex-col items-center">
      {/* grid 셀(1fr)을 채우고 높이는 aspect-ratio 로 고정 → 카드가 폭 따라 커져도 이미지
          영역은 항상 160/127(Figma) 비율 유지 */}
      <div className="relative aspect-[160/127] w-full">
        <Image
          loading="lazy"
          src={item.img}
          alt={item.label}
          fill
          sizes="(min-width: 1024px) 141px, (min-width: 640px) 25vw, (min-width: 500px) 33vw, 50vw"
          className="object-contain p-3 laptop:p-2.5"
        />
      </div>
      <p className="text-center text-xs leading-4 tracking-[0.4px] text-coolgray-90">
        {item.label}
      </p>

      {item.tooltip && (
        <HeadsetInfoTooltip label={item.label} text={item.tooltip} />
      )}
    </div>
  );
};

/*
 카드 우상단 ⓘ 툴팁. tooltip 이 있는 카드(현재 PICO Neo 3)에서만 mount 되므로
 useTrans 를 여기서만 호출 → 모든 카드가 훅을 호출하던 낭비 제거.
 */
const HeadsetInfoTooltip = ({
  label,
  text,
}: {
  label: string;
  text: string;
}) => {
  const { t } = useTrans(["games"]);

  return (
    <Tooltip
      variant="dark"
      side="top"
      portal
      // Sheet 패널(z-100)보다 위로 — portal 기본 z-50 이면 가려짐
      className="z-[110]"
      tooltipContent={t(text)}
    >
      <button
        type="button"
        aria-label={`${label} info`}
        className="absolute right-1 top-1 focus:outline-none"
      >
        <InfoIcon className="size-6 text-coolgray-40" />
      </button>
    </Tooltip>
  );
};
